import React, { useEffect } from "react";
import {
	reduxForm,
	Field,
	FormSection,
	getFormValues,
	change,
} from "redux-form";
import { useDispatch, connect, useSelector } from "react-redux";
import {
	StepButtons,
	CustomInput,
	SpinnerLoader,
	PhoneInputRedux,
	AddressSectionRedux,
} from "components";
import styles from "../stepper.module.scss";
import { Row, Col } from "react-bootstrap";
import TaxId from "./taxIdInput";
import CheckboxButton from "./checkboxButton";
import { validateOwner, validateSSN, validateDOB } from "./form";
import {
	onlyDate,
	onlyAlphanumericAndSpecialCharacters,
	trim,
	normalizeAll,
	trimMidSentence,
	normalizeSSN,
	minLengthPhone8,
	maxLengthPhone14,
	required,
} from "validators";
import { ownerActions, uiActions } from "store";

import fp from "lodash/fp";
import { OwnerPayloadType } from "types";
import { getStringWithoutHyphens } from "helpers";
import { mapAddressToApi } from "mappers";

const formName = "owner";

let OwnerStep = ({
	prevStep,
	nextStep,
	handleSubmit,
	submitting,
	invalid,
	showSpinnerOverlay,
	hideSpinnerOverlay,
	sendOwner,
	showErrorNotification,
	token,
	formValues,
}) => {
	const dispatch = useDispatch();

	useEffect(() => {
		if (submitting) {
			showSpinnerOverlay();
		}
		return hideSpinnerOverlay;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [submitting]);

	const applicationId = useSelector(
		(state: any) => state.generalInfo.applicationId,
	);

	const saveOwner = (values: OwnerPayloadType) => {
		if (invalid) {
			showErrorNotification({ title: "Validation failed" });
			return;
		}

		const ownerLocation = mapAddressToApi?.(values.location);

		let payload = {
			phone_number: values.phoneNumber,
			name: values.name,
			dob: values.dob,
			ssn: getStringWithoutHyphens(values.SSN),
			...ownerLocation,
			proceed_without_tin: values.proceedWithoutTIN,
			token,
			applicationId,
		};

		return sendOwner(payload).then(nextStep);
	};

	const handleNewTin = () => {
		dispatch(change(formName, "proceedWithoutTIN", false));
	};

	const checkboxField = (
		<Field
			label="I want to provide this later"
			component={CheckboxButton}
			id="proceedWithoutTIN"
			name="proceedWithoutTIN"
			labelClassName={styles.skipTaxIdLabel}
			wrapperClassName={styles.subOption}
			hideRequiredMark={true}
			tabIndex={3}
		/>
	);

	return (
		<form onSubmit={handleSubmit(saveOwner)} className={styles.form}>
			<Row>
				<Col lg={6} md={12}>
					<Field
						component={CustomInput}
						name="name"
						label="Owner Name"
						loader={SpinnerLoader}
						validate={validateOwner}
						normalizeOnBlur={trim}
						normalize={normalizeAll([
							trimMidSentence,
							onlyAlphanumericAndSpecialCharacters,
						])}
						tabIndex={1}
					/>
				</Col>
				<Col lg={6} md={12}>
					<Field
						component={TaxId}
						label="SSN"
						validate={validateSSN}
						normalize={normalizeSSN}
						name="SSN"
						onChange={handleNewTin}
						loader={SpinnerLoader}
						hideRequiredMark={false}
						helperLabel="As a financial institution, we are required per the
						Patriot Act to verify the identity of any individual or
						corporation opening a new merchant account. A valid SSN
						is required."
						additionalField={checkboxField}
						tabIndex={2}
					/>
				</Col>
			</Row>
			<hr className={styles.divider} />
			<div>
				<h1 className={styles.header}>Home Address</h1>

				<FormSection name="location">
					<AddressSectionRedux
						formName={formName}
						sectionName="location.address"
						isRequired
					/>
				</FormSection>
			</div>
			<hr className={styles.divider} />
			<Row className="mb-4">
				<Col lg={6} md={12}>
					<Field
						component={PhoneInputRedux}
						name="phoneNumber"
						label="Owner Phone Number"
						isRequired
						validate={[required, minLengthPhone8, maxLengthPhone14]}
						formName={formName}
					/>
				</Col>
				<Col lg={6} md={12}>
					<Field
						component={CustomInput}
						name="dob"
						label="Owner DOB"
						validate={validateDOB}
						normalize={onlyDate}
						placeholder="mm/dd/yyyy"
						tabIndex={10}
					/>
				</Col>
			</Row>
			<StepButtons
				nextStep={nextStep}
				prevStep={prevStep}
				initialTabIndex={11}
			/>
		</form>
	);
};

const mapStateToProps = (state: any) => ({
	formValues: getFormValues(formName)(state) || {},
});

const mapDispatchToProps = {
	sendOwner: ownerActions.sendOwner,
	showSuccessNotification: uiActions.showSuccessNotification,
	showErrorNotification: uiActions.showErrorNotification,
	showInfoNotification: uiActions.showInfoNotification,
	showSpinnerOverlay: uiActions.showSpinnerOverlay,
	hideSpinnerOverlay: uiActions.hideSpinnerOverlay,
};

const OwnerStepWithForm = reduxForm({
	form: formName,
	destroyOnUnmount: false,
})(OwnerStep);

const OwnerStepConnected = fp.flow(
	connect(mapStateToProps, mapDispatchToProps),
)(OwnerStepWithForm);

export { OwnerStepConnected as OwnerStep };
